export function fileDisplayName(file) {
    return (file && (
        file.name
        || file.originalName
        || file.original_name
        || file.file_name
        || file.filename
        || file.title
    )) || `Файл`;
}

export function fileMimeType(file) {
    return String(file && (file.mimeType || file.mime_type || file.type) || ``).toLowerCase();
}

export function fileExtension(file) {
    const name = fileDisplayName(file);
    const match = String(name || ``).toLowerCase().match(/\.([a-z0-9]{1,8})$/i);
    return match ? match[1] : ``;
}

export function fileFormatLabel(file) {
    const extension = fileExtension(file);
    if (extension) {
        return extension.toUpperCase();
    }

    const mime = fileMimeType(file);
    if (mime.indexOf(`pdf`) !== -1) {
        return `PDF`;
    }
    if (mime.indexOf(`word`) !== -1 || mime.indexOf(`document`) !== -1) {
        return `DOC`;
    }
    if (mime.indexOf(`excel`) !== -1 || mime.indexOf(`spreadsheet`) !== -1) {
        return `XLS`;
    }
    if (mime.indexOf(`zip`) !== -1 || mime.indexOf(`archive`) !== -1) {
        return `ZIP`;
    }
    if (mime.indexOf(`video/`) === 0) {
        return `VIDEO`;
    }
    if (mime.indexOf(`audio/`) === 0) {
        return `AUDIO`;
    }
    if (mime.indexOf(`text`) !== -1) {
        return `TXT`;
    }
    return `FILE`;
}

export function fileIconClass(file) {
    if (isImageFile(file)) {
        return `fas fa-file-image`;
    }

    const label = fileFormatLabel(file).toLowerCase();
    if (label === `pdf`) {
        return `fas fa-file-pdf`;
    }
    if ([`doc`, `docx`, `rtf`].indexOf(label) !== -1) {
        return `fas fa-file-word`;
    }
    if ([`xls`, `xlsx`, `csv`].indexOf(label) !== -1) {
        return `fas fa-file-excel`;
    }
    if ([`zip`, `rar`, `7z`].indexOf(label) !== -1) {
        return `fas fa-file-archive`;
    }
    if ([`video`, `mp4`, `mov`, `avi`, `mkv`, `webm`].indexOf(label) !== -1) {
        return `fas fa-file-video`;
    }
    if ([`audio`, `mp3`, `wav`, `ogg`].indexOf(label) !== -1) {
        return `fas fa-file-audio`;
    }
    if ([`txt`, `log`, `md`].indexOf(label) !== -1) {
        return `fas fa-file-alt`;
    }
    return `fas fa-file`;
}

export function isImageFile(file) {
    if (!file) {
        return false;
    }
    if (file.isImage === true || file.is_image === true) {
        return true;
    }
    const mime = fileMimeType(file);
    const name = fileDisplayName(file).toLowerCase();
    return mime.indexOf(`image/`) === 0 || /\.(png|jpe?g|jfif|gif|webp|bmp)$/i.test(name);
}

export function isVideoFile(file) {
    if (!file) {
        return false;
    }
    if (file.isVideo === true || file.is_video === true) {
        return true;
    }
    const mime = fileMimeType(file);
    const name = fileDisplayName(file).toLowerCase();
    return mime.indexOf(`video/`) === 0 || /\.(mp4|mov|m4v|avi|webm|mkv|3gp)$/i.test(name);
}

export function formatFileSize(bytes) {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) {
        return ``;
    }
    const sizes = [`Б`, `КБ`, `МБ`, `ГБ`];
    let index = Math.floor(Math.log(value) / Math.log(1024));
    if (index < 0) {
        index = 0;
    }
    if (index >= sizes.length) {
        index = sizes.length - 1;
    }
    return `${Math.round(value / Math.pow(1024, index) * 100) / 100} ${sizes[index]}`;
}

export function normalizeFileViewerFiles(files) {
    const list = Array.isArray(files)
        ? files
        : (files && typeof files.length === `number` ? Array.from(files) : files ? [files] : []);

    return list
        .filter(Boolean)
        .map((file, index) => normalizeFileViewerFile(file, index))
        .filter(file => file.url && file.url !== `#`);
}

export function normalizeFileViewerFile(file, index = 0) {
    const raw = file.raw !== undefined ? file.raw : file;
    const viewerNotice = file.viewerNotice || null;
    const isViewerNotice = Boolean(file.isViewerNotice || viewerNotice);
    const name = fileDisplayName(file);
    const mimeType = fileMimeType(file);
    const image = !isViewerNotice && (file.isImage !== undefined ? Boolean(file.isImage) : isImageFile(file));
    const video = !isViewerNotice && (file.isVideo !== undefined ? Boolean(file.isVideo) : isVideoFile(file));
    const url = file.url || file.href || file.downloadUrl || `#`;
    const downloadUrl = file.downloadUrl || url;
    const thumbnailUrl = file.thumbnailUrl || file.previewUrl || (image ? url : null);
    const size = file.size || file.file_size || file.fileSize || null;
    const normalized = {
        id: file.id !== undefined && file.id !== null ? file.id : `${index}_${name}`,
        key: file.key || `${file.id !== undefined && file.id !== null ? file.id : index}_${name}`,
        name,
        mimeType,
        size,
        sizeText: formatFileSize(size),
        url,
        downloadUrl,
        thumbnailUrl,
        isImage: image,
        isVideo: video,
        isViewerNotice,
        viewerNotice,
        galleryShiftId: file.galleryShiftId || null,
        galleryShiftIndex: file.galleryShiftIndex,
        galleryShiftTitle: file.galleryShiftTitle || null,
        reportAnnulled: Boolean(file.reportAnnulled),
        reportAnnulmentReason: file.reportAnnulmentReason || null,
        formatLabel: fileFormatLabel(file),
        iconClass: fileIconClass(file),
        raw
    };

    normalized.placeholderUrl = file.placeholderUrl || fileViewerPlaceholderUrl(normalized);
    return normalized;
}

export function resolveFileViewerStartIndex(files, options = {}) {
    if (
        Number.isInteger(options.startIndex)
        && options.startIndex >= 0
        && options.startIndex < files.length
    ) {
        return options.startIndex;
    }

    if (options.startId !== undefined && options.startId !== null) {
        const index = files.findIndex(file => String(file.id) === String(options.startId));
        if (index >= 0) {
            return index;
        }
    }

    return 0;
}

export function fileViewerPlaceholderUrl(file) {
    const label = escapeSvgText(file.formatLabel || `FILE`);
    const name = escapeSvgText(file.name || `Файл`);
    const size = escapeSvgText(file.sizeText || ``);
    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">`,
        `<rect width="1200" height="800" fill="#05070a"/>`,
        `<rect x="426" y="118" width="348" height="438" rx="24" fill="#f8f9fa" stroke="#dee2e6" stroke-width="8"/>`,
        `<path d="M684 118v122h90" fill="none" stroke="#ced4da" stroke-width="8" stroke-linejoin="round"/>`,
        `<rect x="456" y="334" width="288" height="98" rx="14" fill="#0d6efd"/>`,
        `<text x="600" y="400" text-anchor="middle" font-family="Arial, sans-serif" font-size="50" font-weight="700" fill="#ffffff">${label}</text>`,
        `<text x="600" y="650" text-anchor="middle" font-family="Arial, sans-serif" font-size="27" fill="#ffffff">${name}</text>`,
        size ? `<text x="600" y="690" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" fill="#adb5bd">${size}</text>` : ``,
        `</svg>`
    ].join(``);

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function escapeSvgText(value) {
    return String(value || ``)
        .replace(/&/g, `&amp;`)
        .replace(/</g, `&lt;`)
        .replace(/>/g, `&gt;`)
        .replace(/"/g, `&quot;`);
}

export function withQueryParams(url, params) {
    if (!url || url === `#`) {
        return url;
    }
    const entries = Object.keys(params || {})
        .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== ``)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
    if (!entries.length) {
        return url;
    }
    const separator = String(url).indexOf(`?`) === -1 ? `?` : `&`;
    return `${url}${separator}${entries.join(`&`)}`;
}
